import type { ParagraphStyle, CharacterStyle, StylesRegistry } from '../../EdgeDocsCore/models/styles';
import type { LegalRun } from '../../EdgeDocsCore/models/ast';
import { resolveFont, type ResolvedStyle } from '../measurement/FontResolver';
import { getFontMetrics, computeLineHeightMultiple } from '../measurement/FontMetrics';
import { getStylesRegistry } from './registry';

/**
 * Resolves the final, effective style for a LegalRun by applying inheritance
 * from paragraph styles and run-specific overrides.
 */
export function resolveRunStyle(paragraphStyle: ParagraphStyle | undefined, run: LegalRun): ResolvedStyle {
  const registry: StylesRegistry = getStylesRegistry();

  // 1) Start with the default paragraph character style
  let characterStyle: CharacterStyle | undefined;
  if (paragraphStyle?.defaultCharacterStyleId) {
    characterStyle = registry.characterStyles[paragraphStyle.defaultCharacterStyleId];
  }

  // 2) Apply run-specific overrides
  if (run.charStyleId && registry.characterStyles[run.charStyleId]) {
    characterStyle = { ...(characterStyle || {}), ...registry.characterStyles[run.charStyleId] };
  }

  // 3) Fallback safety net
  if (!characterStyle) {
    characterStyle = { id: 'default', fontFamily: 'Times New Roman', fontSizePt: 12 };
  }

  // 4) Resolve font (style) and metrics
  const resolvedFont: ResolvedStyle = resolveFont({
    fontFamily: characterStyle.fontFamily,
    fontSizePt: characterStyle.fontSizePt,
    fontWeight: characterStyle.fontWeight,
    fontStyle: characterStyle.fontStyle,
    colorHex: characterStyle.colorHex,
    openTypeFeatures: characterStyle.openTypeFeatures,
  });

  const metrics = getFontMetrics(resolvedFont);
  const fontSizePx = Math.max(1, resolvedFont.fontSizePt * (96 / 72));
  const multiple = paragraphStyle?.lineSpacingMultiple ?? computeLineHeightMultiple(metrics, fontSizePx);

  return { ...resolvedFont, lineHeight: multiple };
}


